<template>
    <div class="follow-up-container">
      <!-- 顶部标题 -->
      <div class="header">
        <!-- <h1>医院随访预约管理系统</h1> -->
        <div class="user-info">
          <span>{{ patientInfo.name }}</span>
          <span class="separator">|</span>
          <span>{{ patientInfo.id }}</span>
        </div>
      </div>
  
      <!-- 功能导航栏 -->
      <div class="nav-tabs">
        <div 
          v-for="(tab, index) in tabs" 
          :key="index" 
          :class="['tab', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          {{ tab.name }}
        </div>
      </div>
  
      <!-- 主要内容区域 -->
      <div class="content-area">
        <!-- 我的随访计划列表 -->
        <div v-if="activeTab === 'myPlans'" class="my-plans">
          <h2>我的随访计划</h2>
          <div class="search-bar">
            <input type="text" v-model="searchKeyword" placeholder="搜索随访计划...">
            <select v-model="statusFilter">
              <option value="all">全部状态</option>
              <option value="upcoming">即将到期</option>
              <option value="completed">已完成</option>
              <option value="missed">已过期</option>
            </select>
          </div>
          
          <table class="plans-table">
            <thead>
              <tr>
                <th>随访类型</th>
                <th>预约时间</th>
                <th>随访医生</th>
                <th>科室</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(plan, index) in filteredPlans" :key="index" :class="plan.status">
                <td>{{ plan.type }}</td>
                <td>{{ plan.date }}</td>
                <td>{{ plan.doctor }}</td>
                <td>{{ plan.department }}</td>
                <td>
                  <span :class="['status', plan.status]">{{ getStatusText(plan.status) }}</span>
                </td>
                <td>
                  <button v-if="plan.status === 'upcoming'" @click="reschedulePlan(plan)">改期</button>
                  <button v-if="plan.status === 'upcoming'" @click="cancelPlan(plan)">取消</button>
                  <button v-if="plan.status === 'completed'" @click="viewDetail(plan)">查看详情</button>
                </td>
              </tr>
            </tbody>
          </table>
          
          <div v-if="filteredPlans.length === 0" class="no-data">
            暂无符合条件的随访计划
          </div>
        </div>
  
        <!-- 添加随访计划 -->
        <div v-if="activeTab === 'addPlan'" class="add-plan">
          <h2>添加随访计划</h2>
          <div class="form-container">
            <div class="form-group">
              <label>随访类型：</label>
              <select v-model="newPlan.type">
                <option value="">请选择随访类型</option>
                <option value="常规随访">常规随访</option>
                <option value="血压监测">血压监测</option>
                <option value="血糖监测">血糖监测</option>
                <option value="术后随访">术后随访</option>
                <option value="用药随访">用药随访</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>期望日期：</label>
              <input type="date" v-model="newPlan.date">
            </div>
            
            <div class="form-group">
              <label>期望时间段：</label>
              <select v-model="newPlan.timeSlot">
                <option value="">请选择时间段</option>
                <option value="上午">上午(8:00-12:00)</option>
                <option value="下午">下午(13:30-17:30)</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>选择科室：</label>
              <select v-model="newPlan.department" @change="filterDoctors">
                <option value="">请选择科室</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>选择医生：</label>
              <select v-model="newPlan.doctor" :disabled="!newPlan.department">
                <option value="">请选择医生</option>
                <option v-for="doctor in availableDoctors" :key="doctor" :value="doctor">{{ doctor }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>随访备注：</label>
              <textarea v-model="newPlan.notes" placeholder="请填写随访相关事项或特殊需求..."></textarea>
            </div>
            
            <div class="form-actions">
              <button class="btn-submit" @click="submitPlan">提交随访计划</button>
              <button class="btn-reset" @click="resetPlan">重置</button>
            </div>
          </div>
        </div>
  
        <!-- 预约专业人员 -->
        <div v-if="activeTab === 'createAppointment'" class="create-appointment">
          <h2>预约专业人员</h2>
          <div class="tips">
            如果现有的随访计划无法满足您的需求，可以预约专业医护人员进行一对一随访
          </div>
          
          <div class="form-container">
            <div class="form-group">
              <label>预约类型：</label>
              <select v-model="appointment.type">
                <option value="">请选择预约类型</option>
                <option value="个管师随访">个管师随访</option>
                <option value="护士随访">护士随访</option>
                <option value="心理咨询">心理咨询</option>
                <option value="营养指导">营养指导</option>
                <option value="康复指导">康复指导</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>预约方式：</label>
              <div class="radio-group">
                <label>
                  <input type="radio" v-model="appointment.method" value="online">
                  线上视频
                </label>
                <label>
                  <input type="radio" v-model="appointment.method" value="phone">
                  电话咨询
                </label>
                <label>
                  <input type="radio" v-model="appointment.method" value="visit">
                  上门随访
                </label>
              </div>
            </div>
            
            <div class="form-group">
              <label>期望日期：</label>
              <input type="date" v-model="appointment.date">
            </div>
            
            <div class="form-group">
              <label>期望时间段：</label>
              <select v-model="appointment.timeSlot">
                <option value="">请选择时间段</option>
                <option value="上午">上午(8:00-12:00)</option>
                <option value="下午">下午(13:30-17:30)</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>选择专业人员：</label>
              <select v-model="appointment.staff" :disabled="!appointment.type">
                <option value="">请选择专业人员</option>
                <option v-for="staff in filteredStaff" :key="staff.id" :value="staff.name">
                  {{ staff.name }} ({{ staff.title }})
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>预约目的：</label>
              <textarea v-model="appointment.purpose" placeholder="请详细描述您的随访需求..."></textarea>
            </div>
            
            <div class="form-actions">
              <button class="btn-submit" @click="submitAppointment">提交预约申请</button>
              <button class="btn-reset" @click="resetAppointment">重置</button>
            </div>
          </div>
        </div>
      </div>
  
      <!-- 弹窗 -->
      <div class="modal" v-if="showModal">
        <div class="modal-content">
          <span class="close" @click="showModal = false">&times;</span>
          <h3>{{ modalTitle }}</h3>
          <div v-html="modalContent"></div>
          <div class="modal-footer">
            <button @click="showModal = false">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'FollowUpAppointment',
    data() {
      return {
        // 当前患者信息
        patientInfo: {
          name: '许昊然',
          id: 'P20240410001',
          age: 45,
          gender: '男',
          phone: '13800138000'
        },
        
        // 导航标签
        tabs: [
          { id: 'myPlans', name: '我的随访计划' },
          { id: 'addPlan', name: '添加随访计划' },
          { id: 'createAppointment', name: '预约专业人员' }
        ],
        activeTab: 'myPlans',
        
        // 搜索和筛选
        searchKeyword: '',
        statusFilter: 'all',
        
        // 随访计划列表（假数据）
        followUpPlans: [
          { 
            id: 1, 
            type: '血压监测', 
            date: '2025-04-15 09:30', 
            doctor: '李医生', 
            department: '心内科', 
            status: 'upcoming',
            notes: '请空腹前来，带上之前的检查报告' 
          },
          { 
            id: 2, 
            type: '常规随访', 
            date: '2025-04-20 14:00', 
            doctor: '王医生', 
            department: '内分泌科', 
            status: 'upcoming',
            notes: '需要复查血糖指标' 
          },
          { 
            id: 3, 
            type: '术后随访', 
            date: '2025-03-25 10:30', 
            doctor: '赵医生', 
            department: '外科', 
            status: 'completed',
            notes: '术后伤口恢复良好' 
          },
          { 
            id: 4, 
            type: '用药随访', 
            date: '2025-03-01 15:00', 
            doctor: '刘医生', 
            department: '神经内科', 
            status: 'missed',
            notes: '未按时前来随访' 
          }
        ],
        
        // 添加随访计划表单
        newPlan: {
          type: '',
          date: '',
          timeSlot: '',
          department: '',
          doctor: '',
          notes: ''
        },
        
        // 预约专业人员表单
        appointment: {
          type: '',
          method: 'online',
          date: '',
          timeSlot: '',
          staff: '',
          purpose: ''
        },
        
        // 科室列表
        departments: ['心内科', '内分泌科', '神经内科', '外科', '骨科', '妇产科', '儿科', '耳鼻喉科'],
        
        // 医生列表
        doctors: {
          '心内科': ['李医生', '张医生', '吴医生'],
          '内分泌科': ['王医生', '周医生'],
          '神经内科': ['刘医生', '郑医生'],
          '外科': ['赵医生', '钱医生', '孙医生'],
          '骨科': ['陈医生', '杨医生'],
          '妇产科': ['黄医生', '林医生'],
          '儿科': ['朱医生', '徐医生'],
          '耳鼻喉科': ['何医生']
        },
        
        // 专业人员列表
        staffList: [
          { id: 1, name: '王护士', title: '主管护师', type: '护士随访' },
          { id: 2, name: '李护士', title: '护师', type: '护士随访' },
          { id: 3, name: '张个管师', title: '高级个管师', type: '个管师随访' },
          { id: 4, name: '赵个管师', title: '个管师', type: '个管师随访' },
          { id: 5, name: '刘咨询师', title: '心理咨询师', type: '心理咨询' },
          { id: 6, name: '陈营养师', title: '主任营养师', type: '营养指导' },
          { id: 7, name: '杨康复师', title: '康复治疗师', type: '康复指导' }
        ],
        
        // 弹窗
        showModal: false,
        modalTitle: '',
        modalContent: '',
        
        availableDoctors: []
      };
    },
    
    computed: {
      // 过滤后的随访计划
      filteredPlans() {
        return this.followUpPlans
          .filter(plan => {
            // 根据关键词过滤
            const matchKeyword = !this.searchKeyword || 
              plan.type.includes(this.searchKeyword) || 
              plan.doctor.includes(this.searchKeyword) ||
              plan.department.includes(this.searchKeyword);
            
            // 根据状态过滤
            const matchStatus = this.statusFilter === 'all' || plan.status === this.statusFilter;
            
            return matchKeyword && matchStatus;
          });
      },
      
      // 根据所选类型过滤专业人员
      filteredStaff() {
        if (!this.appointment.type) return [];
        return this.staffList.filter(staff => staff.type === this.appointment.type);
      }
    },
    
    methods: {
      // 获取状态文本
      getStatusText(status) {
        const statusMap = {
          'upcoming': '待完成',
          'completed': '已完成',
          'missed': '已过期'
        };
        return statusMap[status] || status;
      },
      
      // 根据科室筛选医生
      filterDoctors() {
        if (this.newPlan.department && this.doctors[this.newPlan.department]) {
          this.availableDoctors = this.doctors[this.newPlan.department];
          this.newPlan.doctor = '';
        } else {
          this.availableDoctors = [];
        }
      },
      
      // 提交随访计划
      submitPlan() {
        // 表单验证
        if (!this.newPlan.type || !this.newPlan.date || !this.newPlan.timeSlot || 
            !this.newPlan.department || !this.newPlan.doctor) {
          this.showMessage('提示', '请填写完整的随访计划信息');
          return;
        }
        
        // 模拟提交
        const fullDate = `${this.newPlan.date} ${this.newPlan.timeSlot === '上午' ? '09:30' : '14:00'}`;
        const newPlanObj = {
          id: this.followUpPlans.length + 1,
          type: this.newPlan.type,
          date: fullDate,
          doctor: this.newPlan.doctor,
          department: this.newPlan.department,
          status: 'upcoming',
          notes: this.newPlan.notes
        };
        
        this.followUpPlans.push(newPlanObj);
        this.showMessage('成功', '随访计划已成功添加！<br>您可以在"我的随访计划"中查看详情。');
        this.resetPlan();
        this.activeTab = 'myPlans';
      },
      
      // 重置随访计划表单
      resetPlan() {
        this.newPlan = {
          type: '',
          date: '',
          timeSlot: '',
          department: '',
          doctor: '',
          notes: ''
        };
        this.availableDoctors = [];
      },
      
      // 提交预约申请
      submitAppointment() {
        // 表单验证
        if (!this.appointment.type || !this.appointment.date || !this.appointment.timeSlot || 
            !this.appointment.staff || !this.appointment.purpose) {
          this.showMessage('提示', '请填写完整的预约信息');
          return;
        }
        
        // 模拟提交
        const appointmentDetails = `
          <p><strong>预约类型：</strong>${this.appointment.type}</p>
          <p><strong>预约方式：</strong>${this.appointment.method === 'online' ? '线上视频' : 
                                      this.appointment.method === 'phone' ? '电话咨询' : '上门随访'}</p>
          <p><strong>预约时间：</strong>${this.appointment.date} ${this.appointment.timeSlot}</p>
          <p><strong>预约人员：</strong>${this.appointment.staff}</p>
          <p><strong>预约目的：</strong>${this.appointment.purpose}</p>
          <p>预约申请已提交，工作人员将在1-2个工作日内与您联系确认。</p>
        `;
        
        this.showMessage('预约成功', appointmentDetails);
        this.resetAppointment();
      },
      
      // 重置预约表单
      resetAppointment() {
        this.appointment = {
          type: '',
          method: 'online',
          date: '',
          timeSlot: '',
          staff: '',
          purpose: ''
        };
      },
      
      // 改期随访计划
      reschedulePlan(plan) {
        this.modalTitle = '改期随访';
        this.modalContent = `
          <p>您正在为以下随访计划申请改期：</p>
          <p><strong>随访类型：</strong>${plan.type}</p>
          <p><strong>原定时间：</strong>${plan.date}</p>
          <p><strong>随访医生：</strong>${plan.doctor} (${plan.department})</p>
          <p class="note">请联系医院客服 0571-12345678 进行改期。</p>
        `;
        this.showModal = true;
      },
      
      // 取消随访计划
      cancelPlan(plan) {
        this.modalTitle = '取消随访';
        this.modalContent = `
          <p>您确定要取消以下随访计划吗？</p>
          <p><strong>随访类型：</strong>${plan.type}</p>
          <p><strong>预约时间：</strong>${plan.date}</p>
          <p><strong>随访医生：</strong>${plan.doctor} (${plan.department})</p>
          <p class="warning">取消后需要重新预约，且可能无法预约到相同的时间段。</p>
          <div class="confirm-buttons">
            <button class="confirm-yes" @click="confirmCancel(${plan.id})">确认取消</button>
            <button class="confirm-no" @click="showModal = false">再考虑一下</button>
          </div>
        `;
        this.showModal = true;
      },
      
      // 确认取消
      confirmCancel(planId) {
        const index = this.followUpPlans.findIndex(plan => plan.id === planId);
        if (index !== -1) {
          this.followUpPlans.splice(index, 1);
        }
        this.showModal = false;
        this.showMessage('提示', '随访计划已取消');
      },
      
      // 查看详情
      viewDetail(plan) {
        this.modalTitle = '随访详情';
        this.modalContent = `
          <p><strong>随访类型：</strong>${plan.type}</p>
          <p><strong>随访时间：</strong>${plan.date}</p>
          <p><strong>随访医生：</strong>${plan.doctor} (${plan.department})</p>
          <p><strong>随访状态：</strong>${this.getStatusText(plan.status)}</p>
          <p><strong>随访备注：</strong>${plan.notes}</p>
          <p><strong>随访结果：</strong>各项指标正常，继续保持当前治疗方案。</p>
          <p><strong>下次随访：</strong>建议三个月后复查。</p>
        `;
        this.showModal = true;
      },
      
      // 显示消息
      showMessage(title, content) {
        this.modalTitle = title;
        this.modalContent = content;
        this.showModal = true;
      }
    }
  };
  </script>
  
  <style scoped>
  .follow-up-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: "Microsoft YaHei", sans-serif;
  }
  
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 20px;
  }
  
  .header h1 {
    font-size: 24px;
    color: #2c3e50;
    margin: 0;
  }
  
  .user-info {
    font-size: 14px;
    color: #666;
  }
  
  .separator {
    margin: 0 10px;
  }
  
  .nav-tabs {
    display: flex;
    margin-bottom: 20px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .tab {
    padding: 10px 20px;
    cursor: pointer;
    position: relative;
  }
  
  .tab.active {
    color: #1890ff;
    font-weight: bold;
  }
  
  .tab.active:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #1890ff;
  }
  
  .content-area {
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
  }
  
  .search-bar {
    display: flex;
    margin-bottom: 15px;
  }
  
  .search-bar input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    margin-right: 10px;
  }
  
  .search-bar select {
    width: 150px;
    padding: 8px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
  }
  
  .plans-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .plans-table th, 
  .plans-table td {
    padding: 12px 8px;
    text-align: left;
    border-bottom: 1px solid #e8e8e8;
  }
  
  .plans-table th {
    background-color: #fafafa;
    font-weight: 500;
  }
  
  .status {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
  }
  
  .status.upcoming {
    background-color: #e6f7ff;
    color: #1890ff;
  }
  
  .status.completed {
    background-color: #f6ffed;
    color: #52c41a;
  }
  
  .status.missed {
    background-color: #fff2f0;
    color: #ff4d4f;
  }
  
  tr.upcoming {
    background-color: #f9fcff;
  }
  
  tr.completed {
    background-color: #fafffe;
  }
  
  tr.missed {
    background-color: #fffafb;
  }
  
  .form-container {
    max-width: 800px;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .form-group input[type="text"],
  .form-group input[type="date"],
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    box-sizing: border-box;
  }
  
  .form-group textarea {
    min-height: 80px;
  }
  
  .radio-group {
    display: flex;
  }
  
  .radio-group label {
    margin-right: 20px;
    font-weight: normal;
    display: flex;
    align-items: center;
  }
  
  .radio-group input {
    margin-right: 5px;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 30px;
  }
  
  .btn-submit {
    background-color: #1890ff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    margin-right: 10px;
  }
  
  .btn-reset {
    background-color: #f5f5f5;
    color: #666;
    border: 1px solid #d9d9d9;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .tips {
    background-color: #f9f9f9;
    padding: 12px;
    margin-bottom: 20px;
    border-left: 4px solid #1890ff;
    color: #666;
  }
  
  .no-data {
    text-align: center;
    padding: 30px;
    color: #999;
  }
  
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 4px;
    min-width: 400px;
    max-width: 600px;
    position: relative;
  }
  
  .close {
    position: absolute;
    top: 10px;
    right: 15px;
    cursor: pointer;
    font-size: 20px;
  }
  
  .modal-footer {
    margin-top: 20px;
    text-align: right;
  }
  
  .modal-footer button {
    padding: 8px 16px;
    background-color: #1890ff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .warning {
    color: #ff4d4f;
  }
  
  .note {
    color: #1890ff;
  }
  
  .confirm-buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
  
  .confirm-yes {
    background-color: #ff4d4f;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    margin-right: 10px;
    cursor: pointer;
  }
  
  .confirm-no {
    background-color: #f5f5f5;
    border: 1px solid #d9d9d9;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  button {
    padding: 4px 8px;
    margin-right: 5px;
    border-radius: 3px;
    border: 1px solid #d9d9d9;
    background-color: #fff;
    cursor: pointer;
  }
  
  button:hover {
    color: #1890ff;
    border-color: #1890ff;
  }
  </style>